import { useQuery } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import { useSession } from '@/lib/auth/session';
import { queryKeys } from '@/lib/queryKeys';
import type { SessionUser, UserScope } from '@/lib/types';

/**
 * "지금 로그인한 사람"에 대해 홈이 알아야 하는 것 — 소속과 인사 정보.
 *
 * 여러 위젯이 같은 답을 필요로 한다. 마이페이지는 인사 정보를, 날씨·식단 위젯은 대표 소속을,
 * 주간보고 할일은 내 현장을 본다. 판단을 위젯마다 따로 두면 같은 사람이 위젯마다 다른 소속으로
 * 보이는 날이 온다.
 *
 * ## 판정은 서버가 한다 (2026-08-05 변경)
 *
 * 예전에는 **사용자 관리 목록**(`GET /v1/users`)을 통째로 받아 내 이메일·이름과 맞춰 소속을 찾았다.
 * 그 목록은 `settings_users` 권한이 있어야 열려서 **일반 사용자에게는 403** 이었다. 그래서 소속이
 * 늘 비었고 주간보고 할일·날씨·식단 기준이 전부 '본사'로 떨어졌다 — 관리자로 로그인하면 정상으로
 * 보여 눈에 띄지 않던 버그다.
 *
 * 지금은 `GET /v1/home/profile` 하나를 읽는다. 인사 명부 연결은 서버가 `hr_personnel.user_id`
 * (비었으면 이메일)로 판정하고 **이름으로는 맞추지 않는다** — 동명이인의 인사 자료가 남에게 열리는
 * 사고가 이름 매칭에서 나온다.
 *
 * 못 맞춰도 화면은 뜬다. 인사 정보가 아직 등록되지 않은 계정이 실제로 있기 때문이다.
 */

/** 서버가 내려주는 **계정** 소속 한 줄(`auth_user_scopes`). 종류가 대문자(DEPARTMENT·SITE)로 온다. */
type ProfileScope = {
  type: 'DEPARTMENT' | 'SITE';
  name: string;
  isPrimary: boolean;
  showInPlacement: boolean;
};

/**
 * **인사 배치표**(`hr_personnel_assignments`) 기준의 소속 한 줄. 사람에게 보여줄 소속은 이쪽이 정본이다.
 *
 * 예전에는 마이페이지가 `/v1/assignments` 를 읽어 그렸는데, 그 경로는 `hr_placement` 권한이 있어야
 * 열려서 **일반 사용자에게는 403** 이었다 — 계정 소속(`scopes`)까지 비어 있는 흔한 경우에는
 * "소속 미등록" 만 보였다. 지금은 서버가 본인 것만 골라 함께 내려준다.
 */
export type ProfileUnit = {
  type: 'DEPARTMENT' | 'SITE';
  name: string;
  roleTitle: string | null;
  teamLead: boolean;
  /** 본직·겸직·파견(V66). 본직이 먼저 온다 — 겸직이 첫 줄에 서면 그것이 소속처럼 읽힌다. */
  assignmentRole: 'PRIMARY' | 'CONCURRENT' | 'DISPATCH';
  /** 발령일. 손으로 넣은 옛 배치는 null. */
  startDate: string | null;
  /** 겸직·파견 종료 예정일. */
  endDate: string | null;
  allowanceEligible: boolean;
};

/**
 * 내 인사발령 한 줄. 인력배치표의 「변경 이력」과 같은 사건을 본인 눈으로 본 것이다 —
 * 남의 발령은 오지 않는다(경로에 사용자 id 가 없다).
 */
export type ProfileOrder = {
  orderType: 'TRANSFER' | 'CONCURRENT' | 'DISPATCH' | 'RELEASE';
  assignmentType: 'DEPARTMENT' | 'SITE';
  fromName: string | null;
  toName: string | null;
  roleTitle: string | null;
  effectiveDate: string;
  endDate: string | null;
  allowanceEligible: boolean;
  allowanceNote: string | null;
  /** SCHEDULED = 예정. 발령일이 아직 오지 않았다 — 그날 00시에 소속이 바뀐다(V247). */
  status: 'SCHEDULED' | 'CONFIRMED' | 'CANCELLED';
};

/**
 * 수당 대상 요약 — <b>대상인지와 그 근거</b>까지다. 금액은 없다.
 *
 * 귀향비 산정(거주지·거리·정책 구간)은 아직 스키마만 있다(DOC-10 §A). 반쯤 계산한 금액을 개인
 * 화면에 띄우면 그 숫자를 급여로 읽는 사람이 생기므로, 화면은 "대상입니다" 까지만 말한다.
 */
export type ProfileAllowance = {
  eligible: boolean;
  unitName: string;
  note: string | null;
  since: string | null;
  /**
   * 이 배치가 시작된 뒤로 <b>아직 확인하지 않았는가</b>. 홈 위젯의 줄은 이 값을 본다 —
   * 한 번 확인하면 접히고, 새 현장으로 발령이 나면 다시 선다(기준이 배치 시작일이다).
   */
  unread: boolean;
};

/** 마이페이지가 그리는 만큼의 인사 정보. 급여·연차·자격증은 여기 없다(`/v1/home/hr`). */
export type ProfilePersonnel = {
  id: string;
  name: string;
  /**
   * 사번 (V349 · 사용자 지시 2026-09-29). 회사가 이미 쓰는 번호이고 값은 <b>인력DB 엑셀</b>로만
   * 들어온다 — 아직 안 들어온 사람은 null 이고 화면은 「-」 로 그린다.
   */
  employeeNo: string | null;
  rank: string | null;
  position: string | null;
  deptSite: string | null;
  joinDate: string | null;
  grade: string | null;
  phone: string | null;
  extension: string | null;
  /** 프로필 사진(만료되는 presigned URL). 아직 올리지 않았으면 null — 화면은 이니셜 아바타를 그린다. */
  photoUrl: string | null;
  /** 직무(분야). 서버가 재시작 전이면 없다. */
  field?: string | null;
  /** 최종학력(V615 · 학력 이력의 가장 높은 줄). 서버가 재시작 전이면 없다. */
  education?: string | null;
};

type HomeProfile = {
  scopes: ProfileScope[];
  units: ProfileUnit[];
  primaryScope: ProfileScope | null;
  locationName: string;
  personnel: ProfilePersonnel | null;
  orders: ProfileOrder[];
  allowance: ProfileAllowance | null;
};

export type MyProfile = {
  sessionUser: SessionUser | null;
  /** 인사 정보. 인사 명부와 연결되지 않은 계정이면 null. */
  person: ProfilePersonnel | null;
  /** 인사 배치표 기준의 내 소속. 화면에 "내 소속" 으로 보여줄 값은 이것이다. */
  units: ProfileUnit[];
  scopes: UserScope[];
  /** 대표 소속. 날씨·식단·주간보고 위젯이 기준으로 삼는다. */
  primaryScope: UserScope | null;
  /** 대표 소속이 현장이면 현장명, 아니면 본사 부서명. 둘 다 없으면 `본사`. */
  locationName: string;
  /** 내 최근 인사발령(겸직·파견·전보). 취소된 것은 서버가 빼고 준다. */
  orders: ProfileOrder[];
  /** 수당 대상이면 그 요약, 아니면 null. 금액은 없다. */
  allowance: ProfileAllowance | null;
  pending: boolean;
};

export function useMyProfile(): MyProfile {
  const { data: session, isPending: sessionPending } = useSession();
  const sessionUser = session?.user ?? null;

  const profile = useQuery({
    queryKey: queryKeys.homeProfile,
    queryFn: () => apiFetch<HomeProfile>('/v1/home/profile'),
    enabled: Boolean(sessionUser),
  });

  // 소속은 서버가 본인 것만 골라 함께 내려준다. 예전처럼 `/v1/assignments`(전원 목록)를 받아
  // 브라우저에서 거르지 않는다 — 그 경로는 `hr_placement` 권한이 있어야 열려 일반 사용자에게는 403 이었다.
  return {
    sessionUser,
    person: profile.data?.personnel ?? null,
    units: profile.data?.units ?? [],
    scopes: (profile.data?.scopes ?? []).map(toUserScope),
    primaryScope: profile.data?.primaryScope ? toUserScope(profile.data.primaryScope) : null,
    locationName: profile.data?.locationName ?? '본사',
    orders: profile.data?.orders ?? [],
    allowance: profile.data?.allowance ?? null,
    pending: sessionPending || profile.isPending,
  };
}

/**
 * 서버의 대문자 종류를 화면 타입(소문자)으로 맞춘다.
 *
 * 화면 곳곳이 `scopeType === 'site'` 로 비교한다(v1 값 그대로). 비교하는 쪽을 전부 고치는 대신
 * 경계 한 곳에서 바꾼다 — 한 군데라도 빠뜨리면 "현장 사람인데 현장으로 안 잡히는" 조용한 실패가 된다.
 */
function toUserScope(scope: ProfileScope): UserScope {
  return {
    scopeType: scope.type === 'SITE' ? 'site' : 'department',
    scopeName: scope.name,
    primary: scope.isPrimary,
    showInPlacement: scope.showInPlacement,
    /*
      이 경로는 직책을 <b>싣지 않는다</b>(`HomeProfileResponse.Scope` 는 type·name·isPrimary·
      showInPlacement 넷뿐이다). 여기서 그것이 문제가 되지 않는 이유는, 이 값이 마이페이지의
      <b>표시용</b>이고 권한 규칙의 `DUTY` 조건은 서버가 판정하기 때문이다(V119).

      ⚠ 없는 값을 그럴듯하게 지어내지 않는다 — 대표 소속이라고 「팀장」을 넣는 식으로 채우면
      화면이 서버 판정과 다른 말을 하게 된다. 필요해지면 서버 응답에 칸을 먼저 낸다.
    */
    duty: null,
  };
}
